import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { clearTokens, getCurrentUserEmail } from "../../utils/auth";
import { HamburgerMenuIcon, HomeIcon, PersonIcon, GearIcon, ExitIcon, MixerVerticalIcon } from "@radix-ui/react-icons";

const navItems = [
  { to: "/", label: "Overview", icon: HomeIcon, end: true },
  { to: "/organisations", label: "Organisations", icon: PersonIcon, end: false },
  { to: "/bulk-roles", label: "Bulk Roles", icon: MixerVerticalIcon, end: false },
  { to: "/roles", label: "Roles", icon: GearIcon, end: false },
];

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const email = getCurrentUserEmail();

  const handleLogout = () => {
    clearTokens();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen font-arial bg-default-99">
      {/* Sidebar */}
      <aside
        className={`flex flex-col bg-white border-r border-border-gray transition-all duration-200 ${
          sidebarOpen ? "w-56" : "w-14"
        }`}
      >
        {/* Logo / toggle */}
        <div className="flex items-center justify-between px-3 py-4 border-b border-border-gray h-14">
          {sidebarOpen && (
            <span className="text-sm font-bold text-gray-800 truncate">Solentis Admin</span>
          )}
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="p-1.5 rounded hover:bg-default-99 flex-shrink-0 ml-auto"
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <HamburgerMenuIcon className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-default-99 text-gray-900 border-r-2 border-primary-blue"
                    : "text-gray-600 hover:bg-default-99 hover:text-gray-900"
                }`
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {sidebarOpen && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar footer — user + logout */}
        <div className="border-t border-border-gray p-3">
          {sidebarOpen ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-gray-400 truncate" title={email}>
                {email}
              </p>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 rounded text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                <ExitIcon className="h-4 w-4 flex-shrink-0" />
                Sign out
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              title="Sign out"
              className="flex items-center justify-center w-full p-2 rounded text-red-500 hover:bg-red-50 transition-colors"
            >
              <ExitIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-border-gray px-6 h-14 flex items-center justify-between flex-shrink-0">
          <h1 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
            Admin Panel
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-400 hidden sm:block">{email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
            >
              <ExitIcon className="h-4 w-4" />
              <span>Sign out</span>
            </button>
          </div>
        </header>

        <div className="flex-1 p-6 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
